/**
 * FootingAnalysisPage — análisis de zapatas aisladas.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Loader2, Save } from 'lucide-react';
import { FootingIcon } from '@/components/icons/FootingIcon';
import { Button } from '@/components/ui/Button';
import { SaveDesignDialog } from '@/features/designs/SaveDesignDialog';
import { useDesign } from '@/features/designs/useDesigns';
import {
  Card,
  Divider,
  ForceItem,
  NumberField,
  Row,
  RowKV,
  Section,
  WarningsCard,
} from '@/features/analysis/shared/primitives';
import { useFootingAnalysisStore } from '@/features/analysis/footings/store';
import { useFootingAnalysisQuery } from '@/features/analysis/footings/useFootingAnalysis';
import type { FootingAnalysisRequest } from '@/features/analysis/footings/types';

export function FootingAnalysisPage() {
  const request = useFootingAnalysisStore((s) => s.request);
  const setColumn = useFootingAnalysisStore((s) => s.setColumn);
  const setLoads = useFootingAnalysisStore((s) => s.setLoads);
  const setSoil = useFootingAnalysisStore((s) => s.setSoil);
  const setUserDimensions = useFootingAnalysisStore((s) => s.setUserDimensions);
  const setMaterials = useFootingAnalysisStore((s) => s.setMaterials);
  const loadRequest = useFootingAnalysisStore((s) => s.loadRequest);

  const params = useParams<{ id?: string }>();
  const designId =
    params.id && params.id !== 'new' && /^\d+$/.test(params.id) ? Number(params.id) : null;
  const designQuery = useDesign(designId);
  useEffect(() => {
    if (designQuery.data) {
      loadRequest(designQuery.data.payload as unknown as FootingAnalysisRequest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designQuery.data?.id]);

  const { data, isFetching } = useFootingAnalysisQuery(request);
  const navigate = useNavigate();
  const [saveOpen, setSaveOpen] = useState(false);

  const onDesign = () => {
    if (!data) return;
    navigate('/app/footings/new', {
      state: {
        prefill: {
          source: 'analysis',
          analysisId: designId,
          section: {
            Lx: data.design_prefill.Lx_cm,
            Ly: data.design_prefill.Ly_cm,
            Hf: data.design_prefill.Hf_cm,
            bc: data.design_prefill.bc_cm,
            hc: data.design_prefill.hc_cm,
            df: data.design_prefill.df_cm,
          },
          materials: { fc: request.fc, fy: request.fy, cover: request.cover_cm },
          soil: { sigma_adm: data.design_prefill.sigma_adm_kgf_cm2 },
          loads: {
            P: data.design_prefill.P_service,
            Mx: data.design_prefill.Mx_service,
            My: data.design_prefill.My_service,
            Pu: data.design_prefill.Pu_factored,
            Mux: data.design_prefill.Mux_factored,
            Muy: data.design_prefill.Muy_factored,
          },
        },
      },
    });
  };

  return (
    <div className="flex h-full flex-col">
      <header
        className="flex h-14 shrink-0 items-center justify-between border-b px-6"
        style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
      >
        <div>
          <div
            className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em]"
            style={{ color: 'var(--text-muted)' }}
          >
            <FootingIcon size={12} strokeWidth={1.8} /> Análisis E.050 + E.060
          </div>
          <div className="text-sm font-semibold leading-none" style={{ color: 'var(--text-primary)' }}>
            Análisis de zapatas aisladas
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setSaveOpen(true)}>
            <Save size={14} strokeWidth={1.8} /> {designId ? 'Actualizar' : 'Guardar'}
          </Button>
          <Button variant="primary" size="md" onClick={onDesign} disabled={!data || isFetching}>
            Diseñar zapata <ArrowRight size={14} strokeWidth={2} />
          </Button>
        </div>
      </header>

      <SaveDesignDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        module="analysis_zapatas"
        payload={request as unknown as Record<string, unknown>}
        existing={designQuery.data ?? null}
      />

      <div className="grid flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[340px_minmax(0,1fr)]">
        <div
          className="overflow-y-auto border-r p-5 scrollbar-thin"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
        >
          <Section title="Columna que apoya">
            <Row>
              <NumberField label="bc (cm)" value={request.column.bc_cm} onChange={(v) => setColumn({ bc_cm: v })} />
              <NumberField label="hc (cm)" value={request.column.hc_cm} onChange={(v) => setColumn({ hc_cm: v })} />
            </Row>
          </Section>

          <Section title="Reacciones de servicio">
            <Row>
              <NumberField label="P_D (kgf)" value={request.loads.P_D} onChange={(v) => setLoads({ P_D: v })} step={500} />
              <NumberField label="P_L (kgf)" value={request.loads.P_L} onChange={(v) => setLoads({ P_L: v })} step={500} />
            </Row>
            <Row>
              <NumberField label="Mx_D (kgf·cm)" value={request.loads.Mx_D} onChange={(v) => setLoads({ Mx_D: v })} step={1000} />
              <NumberField label="Mx_L (kgf·cm)" value={request.loads.Mx_L} onChange={(v) => setLoads({ Mx_L: v })} step={1000} />
            </Row>
            <Row>
              <NumberField label="My_D (kgf·cm)" value={request.loads.My_D} onChange={(v) => setLoads({ My_D: v })} step={1000} />
              <NumberField label="My_L (kgf·cm)" value={request.loads.My_L} onChange={(v) => setLoads({ My_L: v })} step={1000} />
            </Row>
          </Section>

          <Section title="Suelo">
            <Row>
              <NumberField
                label="σ_adm (kgf/cm²)"
                value={request.soil.sigma_adm_kgf_cm2}
                onChange={(v) => setSoil({ sigma_adm_kgf_cm2: v })}
                step={0.1}
              />
              <NumberField
                label="df (m)"
                value={request.soil.df_m}
                onChange={(v) => setSoil({ df_m: v })}
                step={0.1}
              />
            </Row>
            <NumberField
              label="γ_suelo (kgf/m³)"
              value={request.soil.gamma_soil_kgf_m3}
              onChange={(v) => setSoil({ gamma_soil_kgf_m3: v })}
              step={50}
            />
          </Section>

          <Section title="Predimensionamiento (opcional)">
            <Row>
              <NumberField
                label="B (m, 0=auto)"
                value={request.B_m_user ?? 0}
                onChange={(v) => setUserDimensions({ B_m_user: v > 0 ? v : null })}
                step={0.05}
              />
              <NumberField
                label="L (m, 0=auto)"
                value={request.L_m_user ?? 0}
                onChange={(v) => setUserDimensions({ L_m_user: v > 0 ? v : null })}
                step={0.05}
              />
            </Row>
            <NumberField
              label="h (m, 0=auto)"
              value={request.h_m_user ?? 0}
              onChange={(v) => setUserDimensions({ h_m_user: v > 0 ? v : null })}
              step={0.05}
            />
          </Section>

          <Section title="Materiales">
            <Row>
              <NumberField label="f'c" value={request.fc} onChange={(v) => setMaterials({ fc: v })} />
              <NumberField label="fy" value={request.fy} onChange={(v) => setMaterials({ fy: v })} />
            </Row>
          </Section>
        </div>

        <div className="overflow-y-auto p-6 scrollbar-thin">
          {data ? (
            <>
              <Card title="Dimensiones">
                <Row>
                  <div>
                    <RowKV k="B" v={data.dimensions.B_m} unit="m" bold accent />
                    <RowKV k="L" v={data.dimensions.L_m} unit="m" bold accent />
                    <RowKV k="h" v={data.dimensions.h_m} unit="m" bold />
                  </div>
                  <div>
                    <RowKV k="A requerida" v={data.dimensions.A_required_m2} unit="m²" />
                    <RowKV k="A provista" v={data.dimensions.A_provided_m2} unit="m²" />
                  </div>
                </Row>
              </Card>

              <Card title="Presiones bajo zapata">
                <div className="grid grid-cols-2 gap-3 text-[12px] sm:grid-cols-3">
                  <ForceItem label="σ_max serv" value={data.pressures.sigma_max_service} unit="kgf/cm²" />
                  <ForceItem label="σ_min serv" value={data.pressures.sigma_min_service} unit="kgf/cm²" />
                  <ForceItem label="q_u factorada" value={data.pressures.sigma_uniform_ult} unit="kgf/cm²" />
                </div>
                {data.pressures.has_uplift && (
                  <div
                    className="mt-2 rounded border p-2 text-[12px]"
                    style={{
                      background: 'rgba(220,38,38,0.08)',
                      borderColor: 'rgba(220,38,38,0.4)',
                      color: '#fca5a5',
                    }}
                  >
                    ⚠ Hay tracción en la zapata (σ_min &lt; 0). Aumentar B o L.
                  </div>
                )}
              </Card>

              <Card title="Fuerzas de diseño (en cara de columna)">
                <div className="grid grid-cols-2 gap-3 text-[12px] sm:grid-cols-3">
                  <ForceItem label="Pu" value={data.forces.P_u_kgf} unit="kgf" />
                  <ForceItem label="Mu_x" value={data.forces.Mu_x_kgfcm / 100} unit="kgf·m" />
                  <ForceItem label="Mu_y" value={data.forces.Mu_y_kgfcm / 100} unit="kgf·m" />
                  <ForceItem label="Vu 1-way X" value={data.forces.Vu_one_way_x_kgf} unit="kgf" />
                  <ForceItem label="Vu 1-way Y" value={data.forces.Vu_one_way_y_kgf} unit="kgf" />
                  <ForceItem label="Vu punzonado" value={data.forces.Vu_two_way_kgf} unit="kgf" />
                </div>
              </Card>

              <WarningsCard items={data.warnings} />
            </>
          ) : (
            <div className="flex items-center justify-center py-20" style={{ color: 'var(--text-muted)' }}>
              <Loader2 size={20} className="mr-2 animate-spin" /> Calculando…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
